// src/components/workloads/creation/DrawAreaSelector.tsx
// Top-level discovery mode: draw one or more polygons anywhere on the map
// and pull addresses from inside them. Drawing is click-to-place-vertices
// (google.maps.drawing.DrawingManager was removed in Maps JS v3.65), mirroring
// the Maps tool (MapsPage). Address discovery reuses
// addressDatabaseService.getAddressesInPolygonRefined (OSM tagged-address path
// + building-footprint + Google reverse geocode fallback).

import React, { useState, useCallback } from 'react';
import { Pentagon, Loader2, Check, AlertTriangle, Download } from 'lucide-react';
import { Polygon, Polyline, Marker } from '@react-google-maps/api';
import { useWorkloadStore, PolygonTerritory } from '../../../store/workloadStore';
import { useProject } from '../../../contexts/ProjectContext';
import GoogleMapProvider from '../../map/GoogleMapProvider';
import BaseMap from '../../map/BaseMap';
import PlacesAutocomplete from '../../map/PlacesAutocomplete';
import { addressDatabaseService, AddressData } from '../../../services/addressDatabase.service';
import { exportCreationAddressesToCSV } from '../../../utils/addressExport';

interface LatLngLiteral {
  lat: number;
  lng: number;
}

interface DrawnPolygon {
  id: string;
  paths: LatLngLiteral[];
  bounds: google.maps.LatLngBoundsLiteral;
}

const POLYGON_COLORS = ['#4F46E5', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#BE185D', '#65A30D'];
const colorAt = (i: number) => POLYGON_COLORS[i % POLYGON_COLORS.length];

const boundsOf = (paths: LatLngLiteral[]): google.maps.LatLngBoundsLiteral => {
  const b = new google.maps.LatLngBounds();
  paths.forEach((p) => b.extend(p));
  return b.toJSON();
};

const DrawAreaSelector: React.FC = () => {
  const {
    creation,
    setCreationTerritory,
    setCreationAddresses,
    addCreationPolygon,
    clearCreationPolygons,
  } = useWorkloadStore();

  const { selectedProject } = useProject();
  const schoolLocation = selectedProject?.schoolAddress?.location;

  const defaultCenter: LatLngLiteral = schoolLocation
    ? { lat: schoolLocation.lat, lng: schoolLocation.lng }
    : { lat: 39.8283, lng: -98.5795 };

  const [mapCenter, setMapCenter] = useState<LatLngLiteral>(defaultCenter);
  const [isDrawing, setIsDrawing] = useState(false);
  const [vertices, setVertices] = useState<LatLngLiteral[]>([]);
  const [polygons, setPolygons] = useState<DrawnPolygon[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePlaceSelect = useCallback((place: { location: LatLngLiteral }) => {
    setMapCenter(place.location);
  }, []);

  // Click-to-place vertices while drawing.
  const handleMapClick = useCallback((pos: LatLngLiteral) => {
    if (!isDrawing) return;
    setVertices((v) => [...v, pos]);
  }, [isDrawing]);

  const finishArea = useCallback(() => {
    if (vertices.length < 3) return;
    const poly: DrawnPolygon = {
      id: `poly_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      paths: vertices,
      bounds: boundsOf(vertices),
    };
    setPolygons((prev) => [...prev, poly]);
    setVertices([]);
    setIsDrawing(false);
    setError(null);
  }, [vertices]);

  const clearAll = useCallback(() => {
    setPolygons([]);
    setVertices([]);
    setIsDrawing(false);
    clearCreationPolygons();
    setCreationAddresses([]);
    setCreationTerritory({ type: null, polygon: null, center: null });
    setError(null);
  }, [clearCreationPolygons, setCreationAddresses, setCreationTerritory]);

  const handleDiscover = useCallback(async () => {
    if (polygons.length === 0) {
      setError('Draw at least one area (3+ points, then "Finish area") before discovering addresses.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setProgressMessage(null);
    clearCreationPolygons();

    try {
      const allAddresses: Array<{
        street: string;
        unit?: string;
        city: string;
        state: string;
        zipCode: string;
        location: { lat: number; lng: number };
        selected: boolean;
        polygonId: string;
        streetName: string;
      }> = [];

      for (let i = 0; i < polygons.length; i++) {
        const poly = polygons[i];
        setProgressMessage(`Discovering addresses in area ${i + 1} of ${polygons.length}…`);

        const territory: PolygonTerritory = { id: poly.id, paths: poly.paths, bounds: poly.bounds };
        addCreationPolygon(territory);

        const result = await addressDatabaseService.getAddressesInPolygonRefined(
          poly.paths,
          (msg) => setProgressMessage(`Area ${i + 1}: ${msg}`)
        );

        for (const addr of result.addresses as AddressData[]) {
          const fullStreet = `${addr.houseNumber} ${addr.street}`.trim();
          allAddresses.push({
            street: fullStreet,
            unit: addr.unit,
            city: addr.city || '',
            state: addr.state || '',
            zipCode: addr.postcode || '',
            location: { lat: addr.lat, lng: addr.lng },
            selected: true,
            polygonId: poly.id,
            streetName: fullStreet.replace(/^\d+\s*/, '').trim(),
          });
        }
      }

      setCreationAddresses(allAddresses);

      // Stamp the territory so later wizard steps know we're in polygon mode.
      setCreationTerritory({ type: 'polygon', polygon: polygons[0].paths, center: null });

      if (allAddresses.length === 0) {
        setError(
          'No addresses found in the drawn area(s). Try drawing a larger area, or use Saturation Point if this is a sparse OSM area.'
        );
      }
    } catch (err: any) {
      console.error('Error discovering addresses in drawn areas:', err);
      setError(err.message || 'Failed to discover addresses');
    } finally {
      setIsLoading(false);
      setProgressMessage(null);
    }
  }, [polygons, clearCreationPolygons, setCreationAddresses, setCreationTerritory, addCreationPolygon]);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Draw Your Canvassing Areas</h2>
        <p className="text-gray-600">
          Draw one or more areas on the map. Each area is searched for addresses using OpenStreetMap
          building footprints and Google reverse geocoding.
        </p>
      </div>

      <div className="mb-4">
        <GoogleMapProvider>
          <PlacesAutocomplete
            onPlaceSelect={handlePlaceSelect}
            placeholder="Search for a city or neighborhood…"
            className="max-w-md"
          />
        </GoogleMapProvider>
      </div>

      <div className="relative">
        <GoogleMapProvider>
          <div className="rounded-lg overflow-hidden border border-gray-200">
            <BaseMap center={mapCenter} zoom={15} height={500} onClick={handleMapClick}>
              {/* In-progress drawing */}
              {isDrawing && vertices.length > 0 && (
                <>
                  <Polyline path={vertices} options={{ strokeColor: '#059669', strokeWeight: 2 }} />
                  {vertices.map((v, i) => (
                    <Marker
                      key={`v-${i}`}
                      position={v}
                      icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 4, fillColor: '#059669', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 1 } as any}
                    />
                  ))}
                </>
              )}
              {/* Completed areas */}
              {polygons.map((p, i) => (
                <Polygon
                  key={p.id}
                  paths={p.paths}
                  options={{ fillColor: colorAt(i), fillOpacity: 0.2, strokeColor: colorAt(i), strokeWeight: 2, clickable: false }}
                />
              ))}
            </BaseMap>
          </div>

          {/* Drawing controls */}
          <div className="absolute bottom-4 left-4 z-10 bg-white rounded-lg shadow-md p-2 flex items-center gap-2">
            {!isDrawing ? (
              <button
                onClick={() => { setIsDrawing(true); setVertices([]); setError(null); }}
                className="flex items-center gap-1 px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
              >
                <Pentagon className="h-4 w-4" /> Draw area
              </button>
            ) : (
              <>
                <span className="text-xs text-gray-600 px-1">Drawing… ({vertices.length} pts)</span>
                <button onClick={finishArea} disabled={vertices.length < 3} className="px-2 py-1.5 bg-emerald-600 text-white rounded text-xs font-medium disabled:opacity-50">Finish area</button>
                <button onClick={() => setVertices((v) => v.slice(0, -1))} disabled={!vertices.length} className="px-2 py-1.5 bg-gray-100 text-gray-700 rounded text-xs disabled:opacity-50">Undo</button>
                <button onClick={() => { setIsDrawing(false); setVertices([]); }} className="px-2 py-1.5 bg-gray-100 text-gray-700 rounded text-xs">Cancel</button>
              </>
            )}
            {polygons.length > 0 && (
              <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                {polygons.length} area{polygons.length !== 1 ? 's' : ''}
              </span>
            )}
            {polygons.length > 0 && !isDrawing && (
              <button onClick={handleDiscover} className="px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 text-xs font-medium">
                Discover addresses
              </button>
            )}
            {(polygons.length > 0 || vertices.length > 0) && (
              <button onClick={clearAll} className="px-2 py-1.5 bg-red-50 text-red-600 rounded text-xs hover:bg-red-100">Clear</button>
            )}
          </div>

          {isLoading && (
            <div className="absolute inset-0 bg-white/75 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
                <p className="mt-2 text-gray-600">Discovering addresses…</p>
                {progressMessage && (
                  <p className="mt-1 text-sm text-indigo-600 font-medium">{progressMessage}</p>
                )}
              </div>
            </div>
          )}
        </GoogleMapProvider>
      </div>

      {error && creation.addresses.length === 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {creation.addresses.length > 0 && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start">
              <Check className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="text-green-800 font-medium">
                  Found {creation.addresses.length.toLocaleString()} addresses
                  {creation.polygons?.length > 0 && ` across ${creation.polygons.length} area${creation.polygons.length !== 1 ? 's' : ''}`}
                </p>
                <p className="text-green-700 text-sm mt-1">
                  Click Next to configure how these addresses will be split into workloads.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => exportCreationAddressesToCSV(creation.addresses, 'draw_area')}
              className="flex items-center gap-1 px-3 py-1 text-xs bg-white border border-green-300 rounded text-green-800 hover:bg-green-100"
            >
              <Download className="w-3 h-3" />
              CSV
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Pentagon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-800">How it works</h4>
            <p className="text-sm text-blue-700 mt-1">
              Click "Draw area", then click points on the map to outline a territory. Click "Finish area" to
              close it (3+ points), and draw more if you want separate territories. Then click "Discover
              addresses" — every building footprint inside your areas gets reverse-geocoded into a real
              street address.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrawAreaSelector;
